import { ChatMessageBubble } from "@/components/domain/chat-message-bubble"
import { MessageAttachment } from "@/components/domain/message-attachment"
import { useCurrentRole } from "@/lib/authz"
import type { TicketMessage } from "@/types/ticket"
import type { ChatBubble } from "@/types/test_lab"

type Props = { messages: TicketMessage[] }

// Tool-call chips are platform internals. Hide them from non-admin
// operators in the production ticket view; designer + test panes still
// see them via ChatMessageBubble's default.
const TOOLCALL_VISIBLE_ROLES = new Set(["admin", "super_admin"])

// Scrollable conversation transcript. Renders through the shared
// ChatMessageBubble so the operator's ticket view matches the Designer +
// Test panes visually. File attachments are unique to tickets — we render
// the chips below the bubble in a sibling row.
export function TicketThread({ messages }: Props) {
  const role = useCurrentRole()
  const showToolCalls = TOOLCALL_VISIBLE_ROLES.has(role ?? "")

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-app-muted">
        No messages yet.
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      {messages.map((m) => (
        <div key={m.id} className="flex flex-col gap-1.5">
          <ChatMessageBubble
            msg={toChatBubble(m)}
            userLabel="Customer"
            assistantLabel="AI"
            showToolCalls={showToolCalls}
          />
          {m.files.length > 0 && (
            <div className="flex flex-wrap items-start gap-2 pl-[60px]">
              {m.files.map((f, i) => (
                <MessageAttachment key={i} file={f} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// TicketMessage is a structural superset of ChatBubble — pass the fields
// ChatBubble actually consumes.
function toChatBubble(m: TicketMessage): ChatBubble {
  return {
    id:          m.id,
    role:        m.role,
    content:     m.content,
    toolCalls:   m.toolCalls,
    toolResults: m.toolResults,
    createdAt:   m.createdAt,
    authorName:  m.authorName,
    avatarUrl:   m.avatarUrl,
  }
}
